import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import indlusLogo from '../../../assets/indlusr.png'
import PasswordStrengthBar from '../../auth/components/PasswordStrengthBar'
import { api } from '../../../shared/services/API'
import { useServerFieldErrors } from '../../../shared/hooks/useServerFieldErrors'
import { getBackendError } from '../../../shared/utils/formErrors'

interface FormData {
    password: string
    confirmPassword: string
}

type Status = 'checking' | 'form' | 'invalid' | 'done'

// O backend valida `novaPassword`; mapeia-se para o campo `password` do form.
const FIELD_MAP = { novaPassword: 'password' }

export default function AdminResetPassword() {
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token') ?? ''

    const [status, setStatus] = useState<Status>('checking')
    const [apiError, setApiError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showPw, setShowPw] = useState(false)

    const {
        register,
        handleSubmit,
        watch,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<FormData>({
        defaultValues: { password: '', confirmPassword: '' },
    })
    useServerFieldErrors(setError, clearErrors, FIELD_MAP)

    const passwordValue = watch('password')

    useEffect(() => {
        if (!token) {
            setStatus('invalid')
            return
        }
        let cancelled = false
        // POST com o token no corpo: no endereço (?token=) ia para os logs do servidor.
        api.post('/api/admin/auth/reset-password/valido', { token })
            .then((res) => {
                if (!cancelled) setStatus(res.data?.valido ? 'form' : 'invalid')
            })
            .catch(() => {
                if (!cancelled) setStatus('invalid')
            })
        return () => {
            cancelled = true
        }
    }, [token])

    const onSubmit = async (data: FormData) => {
        setApiError('')
        setIsSubmitting(true)
        try {
            await api.post('/api/admin/auth/reset-password', { token, novaPassword: data.password })
            setStatus('done')
        } catch (err) {
            // Token entretanto expirado/usado — o backend responde 400 BAD_REQUEST.
            if (getBackendError(err)?.code === 'BAD_REQUEST') {
                setStatus('invalid')
            } else {
                setApiError('Não foi possível redefinir a palavra-passe. Tente novamente.')
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                gap: 3,
            }}
        >
            <Box component="img" src={indlusLogo} alt="Indlus" sx={{ height: 44, width: 'auto' }} />

            <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: '1px solid', borderColor: 'divider', width: '100%', maxWidth: 400 }}>
                {status === 'checking' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary">
                            A validar o link…
                        </Typography>
                    </Box>
                )}

                {status === 'invalid' && (
                    <Box sx={{ py: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                            Link inválido ou expirado
                        </Typography>
                        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                            Este link de redefinição já foi usado ou expirou. Peça um novo para continuar.
                        </Alert>
                        <Button component={Link} to="/forgot-password" variant="contained" size="large" fullWidth>
                            Pedir um novo link
                        </Button>
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Link
                                to="/login"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    color: '#64748B',
                                    textDecoration: 'none',
                                    fontSize: '0.875rem',
                                }}
                            >
                                <ArrowBackIcon sx={{ fontSize: 16 }} />
                                Voltar ao login
                            </Link>
                        </Box>
                    </Box>
                )}

                {status === 'done' && (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                bgcolor: 'brand.teal50',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2.5,
                            }}
                        >
                            <CheckCircleOutlinedIcon sx={{ fontSize: 34, color: 'primary.light' }} />
                        </Box>
                        <Typography variant="h5" sx={{ mb: 1 }}>
                            Palavra-passe redefinida!
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Já pode iniciar sessão com a nova palavra-passe.
                        </Typography>
                        <Button component={Link} to="/login" variant="contained" size="large" fullWidth>
                            Ir para o login
                        </Button>
                    </Box>
                )}

                {status === 'form' && (
                    <>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Definir nova palavra-passe
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Escolha uma palavra-passe com pelo menos 8 caracteres.
                        </Typography>

                        {apiError && (
                            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                                {apiError}
                            </Alert>
                        )}

                        <Box
                            component="form"
                            onSubmit={handleSubmit(onSubmit)}
                            noValidate
                            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
                        >
                            <Box>
                                <TextField
                                    label="Nova palavra-passe"
                                    type={showPw ? 'text' : 'password'}
                                    autoFocus
                                    autoComplete="new-password"
                                    fullWidth
                                    error={!!errors.password}
                                    helperText={errors.password?.message}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPw((v) => !v)}
                                                        edge="end"
                                                        aria-label={showPw ? 'Ocultar palavra-passe' : 'Mostrar palavra-passe'}
                                                    >
                                                        {showPw ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                    {...register('password', {
                                        required: 'A palavra-passe é obrigatória.',
                                        minLength: {
                                            value: 8,
                                            message: 'A palavra-passe deve ter pelo menos 8 caracteres.',
                                        },
                                    })}
                                />
                                <PasswordStrengthBar password={passwordValue} />
                            </Box>

                            <TextField
                                label="Confirmar palavra-passe"
                                type={showPw ? 'text' : 'password'}
                                autoComplete="new-password"
                                fullWidth
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword?.message}
                                {...register('confirmPassword', {
                                    required: 'Confirme a palavra-passe.',
                                    validate: (value) =>
                                        value === passwordValue || 'As palavras-passe não coincidem.',
                                })}
                            />

                            <Button type="submit" variant="contained" size="large" fullWidth disabled={isSubmitting}>
                                {isSubmitting ? 'A guardar…' : 'Redefinir palavra-passe'}
                            </Button>
                        </Box>

                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Link
                                to="/login"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    color: '#64748B',
                                    textDecoration: 'none',
                                    fontSize: '0.875rem',
                                }}
                            >
                                <ArrowBackIcon sx={{ fontSize: 16 }} />
                                Voltar ao login
                            </Link>
                        </Box>
                    </>
                )}
            </Paper>
        </Box>
    )
}
